/**
 * Gespeichertes Kachel-Layout je Mandant und Seite (Frontend-Customizer). Bewusst minimal: nur eine
 * Reihenfolge von Kachel-Keys. Unbekannte/neue Keys hängt das Frontend hinten an — ein altes Layout
 * lässt also nie eine später hinzugekommene Kachel verschwinden.
 */
/** Eine vom Admin selbst angelegte Kachel (freier Shortcut). `icon` ist ein Symbol-Name, den die
 *  Oberfläche auflöst (unbekannt = Standardsymbol); `target` eine interne Route (/…) oder externe URL. */
export interface CustomTile {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: string;
  readonly target: string;
}

export interface UiLayout {
  /** Reihenfolge der Kachel-Keys. */
  readonly order: readonly string[];
  /** Freie Text-Überschreibungen je Text-Key (Überschriften, Kachel-Label/Hint …), leer = Standard. */
  readonly texts: Readonly<Record<string, string>>;
  /** Ausgeblendete Kachel-Keys (werden außerhalb des Anpassen-Modus nicht angezeigt). */
  readonly hidden: readonly string[];
  /** Selbst angelegte Kacheln (Shortcuts) — erscheinen zusätzlich zu den eingebauten. */
  readonly customTiles: readonly CustomTile[];
  /** Kacheln pro Reihe auf breiten Bildschirmen (2–4); enge Bildschirme brechen weiter um. */
  readonly columns: number;
  /** Kachelgröße. */
  readonly tileSize: TileSize;
  /** Eigene Kachelfarbe je Kachel-Key (Hex, ganze Fläche) — unabhängig von der Mandantenfarbe. */
  readonly colors: Readonly<Record<string, string>>;
}

export type TileSize = 'compact' | 'normal' | 'large';

export const emptyUiLayout: UiLayout = { order: [], texts: {}, hidden: [], customTiles: [], columns: 3, tileSize: 'normal', colors: {} };

const MAX_CUSTOM_TILES = 24;
const MAX_COLORS = 60;
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Gültige Hex-Farbe (#rgb oder #rrggbb)? */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR.test(value);
}

function normalizeColors(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const colors: Record<string, string> = {};
  let count = 0;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof key !== 'string' || !key || !isHexColor(raw)) continue;
    colors[key] = raw.toLowerCase();
    if (++count >= MAX_COLORS) break;
  }
  return colors;
}

/** Lesbare Textfarbe (dunkel/hell) für einen Hex-Hintergrund — per Luminanz, damit Text immer lesbar bleibt. */
export function readableTextColor(hex: string): string {
  if (!isHexColor(hex)) return '#ffffff';
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#141821' : '#ffffff';
}

function normalizeCustomTiles(value: unknown): CustomTile[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const tiles: CustomTile[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    const key = typeof entry.key === 'string' ? entry.key.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const str = (input: unknown, cap: number) => (typeof input === 'string' ? input.trim().slice(0, cap) : '');
    tiles.push({
      key: key.slice(0, 64),
      label: str(entry.label, 60),
      hint: str(entry.hint, 80),
      icon: str(entry.icon, 40),
      target: str(entry.target, 300),
    });
    if (tiles.length >= MAX_CUSTOM_TILES) break;
  }
  return tiles;
}

/** Doppelfreie String-Liste aus beliebigem JSON. */
function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const clean: string[] = [];
  for (const key of value) {
    if (typeof key === 'string' && key && !seen.has(key)) { seen.add(key); clean.push(key); }
  }
  return clean;
}

/** Text-Überschreibungen begrenzen: nur String→String, getrimmt, gedeckelt (kein Missbrauch als Ablage). */
const MAX_TEXT_LENGTH = 200;

/** Defensiv aus beliebigem JSON lesen (Vorwärtskompatibilität + kaputte Einträge dürfen nicht crashen). */
export function normalizeUiLayout(value: unknown): UiLayout {
  if (!value || typeof value !== 'object') return emptyUiLayout;
  const order = stringList((value as { order?: unknown }).order);
  const hidden = stringList((value as { hidden?: unknown }).hidden);
  const rawTexts = (value as { texts?: unknown }).texts;
  const texts: Record<string, string> = {};
  if (rawTexts && typeof rawTexts === 'object' && !Array.isArray(rawTexts)) {
    for (const [key, text] of Object.entries(rawTexts as Record<string, unknown>)) {
      if (typeof key === 'string' && key && typeof text === 'string') {
        const trimmed = text.trim().slice(0, MAX_TEXT_LENGTH);
        if (trimmed) texts[key] = trimmed;
      }
    }
  }
  const customTiles = normalizeCustomTiles((value as { customTiles?: unknown }).customTiles);
  const columnsRaw = Number((value as { columns?: unknown }).columns);
  const columns = Number.isFinite(columnsRaw) ? Math.min(4, Math.max(2, Math.round(columnsRaw))) : 3;
  const sizeRaw = (value as { tileSize?: unknown }).tileSize;
  const tileSize: TileSize = sizeRaw === 'compact' || sizeRaw === 'large' ? sizeRaw : 'normal';
  const colors = normalizeColors((value as { colors?: unknown }).colors);
  return { order, texts, hidden, customTiles, columns, tileSize, colors };
}

/** Überschriebenen Text lesen, sonst den Standard (leerer Override zählt als „nicht gesetzt"). */
export function textOverride(layout: UiLayout, key: string, fallback: string): string {
  const value = layout.texts[key];
  return value && value.trim() ? value : fallback;
}

/**
 * Wendet eine gespeicherte Reihenfolge auf eine Kachel-Liste an: bekannte Keys zuerst in der
 * gespeicherten Folge, alle übrigen (neuen) danach in ihrer Ursprungsreihenfolge.
 */
export function applyLayoutOrder<T extends { readonly key: string }>(items: readonly T[], order: readonly string[]): T[] {
  const byKey = new Map(items.map((item) => [item.key, item]));
  const seen = new Set<string>();
  const ordered: T[] = [];
  for (const key of order) {
    const item = byKey.get(key);
    if (item && !seen.has(key)) { ordered.push(item); seen.add(key); }
  }
  for (const item of items) {
    if (!seen.has(item.key)) ordered.push(item);
  }
  return ordered;
}

export interface UiLayoutGateway {
  /** Layout einer Seite je Mandant lesen (jedes Mitglied). */
  loadLayout(tenantId: string, pageKey: string): Promise<UiLayout>;
  /** Layout einer Seite je Mandant speichern (nur Admin). */
  saveLayout(tenantId: string, pageKey: string, layout: UiLayout): Promise<UiLayout>;
}
